import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DropdownSingle, colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// NOTION ICON
// ============================================

export const NotionIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="6" fill="#FFFFFF" stroke="#E5E5E5" />
    <path fill="#000000" d="M9 9h3.6l6.9 9.2V9H22v14h-3.2L11.6 13v10H9V9z" />
  </svg>
);

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const DateRangeHint = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

// End date defaults to today (user can still change or clear it).
const todayIso = () => new Date().toISOString().slice(0, 10);

// oolib's DatePicker returns a Date stringified via .toISOString(), which
// converts the user's LOCAL midnight to UTC
// silently shifts the filter back by the user's TZ offset. We use moment to
// snap to local-day boundaries first, then serialise to UTC for the wire.
export const NotionInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({ providerId: "notion", providerName: "Notion" });

  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [dateRange, setDateRange] = useState([todayIso(), todayIso()]);
  const [startDate, endDate] = dateRange;

  const submitErrorMessage =
    submitError && submitError.sourceId === "notion" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no\s+(notion\s+)?(rows|records|data)\s+(found|to)/i.test(submitErrorMessage);

  const databases = preflightData?.databases || [];
  const canSubmit = Boolean(selectedDatabase?.value);

  const onDisconnect = async () => {
    await handleDisconnect();
    setSelectedDatabase(null);
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    const createdAfter = startDate ? moment(startDate).startOf("day").toISOString() : null;
    const createdBefore = endDate ? moment(endDate).endOf("day").toISOString() : null;
    onSubmit({
      adapterOptions: {
        databaseId: selectedDatabase.value,
        ...(createdAfter ? { createdAfter } : {}),
        ...(createdBefore ? { createdBefore } : {}),
      },
    });
  };

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Notion</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Notion</InterfaceHeader>
        <InterfaceDescription>
          Import rows from a Notion database. You'll be redirected to Notion to
          authorize access and pick which databases to share with Dots, then
          brought back here to choose one and import its rows.
        </InterfaceDescription>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Notion</ButtonPrimary>
        </InterfaceActions>
        <HelpText>
          Dots uses OAuth to access your Notion workspace. We only see databases
          and pages you explicitly share with the integration in Notion — your
          team controls exactly what's visible.
        </HelpText>
      </InterfaceContainer>
    );
  }

  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Notion</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Notion connection to ${connection.providerAccountName} expired or was revoked.`
                : "Your Notion connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Notion</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Notion</InterfaceHeader>
      <InterfaceDescription>
        Each row in the selected Notion database becomes one row in your
        dataset. Properties map to columns; rich text and rollups are flattened
        to plain text.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` to ${connection.providerAccountName}` : ""}
          </strong>
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>
          )}
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>
              Authorized by: {connection.authorizedBy.name}
            </span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Notion"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {preflightError && <ErrorBanner>{preflightError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <div style={{ marginTop: 16 }}>
        <DropdownSingle
          label={adapterConfig?.preflightConfig?.selectLabel || "Select Database"}
          placeholder={
            isPreflightLoading
              ? "Loading databases..."
              : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose a database to import..."
          }
          options={databases.map((db) => ({
            display: db.title || "(untitled database)",
            value: db.id,
            _dbInfo: db,
          }))}
          value={selectedDatabase}
          onChange={(k, v) => setSelectedDatabase(v)}
          readOnly={isPreflightLoading || isLoading}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </div>

      <DateRangeRow>
        <DateRangePicker
          id="notion-date-range"
          label="Date Range (optional)"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          readOnly={isLoading}
        />
      </DateRangeRow>

      <DateRangeHint>
        Leave dates blank to import every row in the selected database. Set a
        range to filter by Notion's created_time (when the row was first added).
      </DateRangeHint>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isPreflightLoading || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Import Rows"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const notionIntegrationMeta = {
  id: "notion",
  name: "Notion",
  description: "Import rows from a Notion database",
};

export default NotionInterface;
